'use client'

import { useState } from 'react'
import Link from 'next/link'
import { Loader2, Check, Brain, Target, Mic, FileText, Users, Zap } from 'lucide-react'

interface Props {
  userEmail?: string
  defaultName?: string
  defaultPhone?: string
  defaultCpf?: string
}

const BENEFITS = [
  {
    icon: Brain,
    title: 'Extração do conhecimento tácito',
    desc: 'Técnicas de Critical Decision Method para externalizar o que você sabe mas nunca documentou. As decisões que toma no automático viram sistema.',
  },
  {
    icon: Mic,
    title: 'Captura da sua voz autoral',
    desc: 'Bordões, metáforas, vocabulário-assinatura, tom de comunicação. Tudo que faz você ser VOCÊ quando fala.',
  },
  {
    icon: FileText,
    title: 'Histórias com alma documentadas',
    desc: 'Sua história de origem, seus cases, seus erros que ensinaram, sua prova inegável. Narrativas com arco completo prontas para usar.',
  },
  {
    icon: Target,
    title: 'Mapa do Ecossistema completo',
    desc: 'Documento final com tudo: identidade, método, público, voz, arsenal de conteúdo, esteira de produtos, iscas sugeridas e funis recomendados.',
  },
  {
    icon: Users,
    title: '15 personas mapeadas na matriz NxC',
    desc: 'Cada persona posicionada por nível de consciência e confiança, com iscas ideais e velocidade de conversão.',
  },
  {
    icon: Zap,
    title: 'Proposta de implantação personalizada',
    desc: 'Cronograma sugerido com ativos, iscas e funis. Tudo conectado. Pronto para executar.',
  },
]

type BillingType = 'PIX' | 'CREDIT_CARD' | 'BOLETO'

export default function SessaoDestravaCheckout({
  userEmail = '',
  defaultName = '',
  defaultPhone = '',
  defaultCpf = '',
}: Props) {
  const [email, setEmail] = useState(userEmail)
  const [name, setName] = useState(defaultName)
  const [phone, setPhone] = useState(defaultPhone)
  const [cpfCnpj, setCpfCnpj] = useState(defaultCpf)
  const [billingType, setBillingType] = useState<BillingType>('PIX')
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [success, setSuccess] = useState<{ invoiceUrl: string | null } | null>(null)

  const isLoggedIn = !!userEmail

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault()
    setLoading(true)
    setError(null)

    try {
      const res = await fetch('/api/checkout/sessao-destrava', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email, name, phone, cpfCnpj, billingType }),
      })
      const data = await res.json()
      if (!res.ok) throw new Error(data.error || 'Erro ao processar pagamento')

      setSuccess({ invoiceUrl: data.invoiceUrl })
      if (data.invoiceUrl) {
        setTimeout(() => { window.location.href = data.invoiceUrl }, 800)
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Erro ao processar')
    } finally {
      setLoading(false)
    }
  }

  if (success) {
    return (
      <div className="max-w-md mx-auto bg-white rounded-2xl border border-stone-200 shadow-sm p-8 text-center">
        <div className="w-14 h-14 rounded-full bg-green-100 flex items-center justify-center mx-auto mb-4">
          <Check className="w-7 h-7 text-green-600" />
        </div>
        <h2 className="text-stone-900 font-bold text-xl mb-2">Cobrança criada!</h2>
        <p className="text-stone-500 text-sm mb-6">
          Você será redirecionado para a página de pagamento. Após confirmar, a
          agenda libera automaticamente para você escolher o horário.
        </p>
        {success.invoiceUrl && (
          <a
            href={success.invoiceUrl}
            className="inline-block bg-indigo-600 hover:bg-indigo-700 text-white font-semibold px-6 py-3 rounded-xl text-sm"
          >
            Ir para o pagamento
          </a>
        )}
      </div>
    )
  }

  return (
    <div className="max-w-5xl mx-auto grid gap-6 lg:grid-cols-5">
      {/* Coluna esquerda: oferta */}
      <div className="lg:col-span-3 space-y-6">
        <div className="bg-white rounded-2xl border border-stone-200 shadow-sm overflow-hidden">
          <div className="bg-gradient-to-br from-indigo-600 to-violet-500 px-8 py-8 text-white">
            <div className="inline-flex items-center gap-2 bg-white/20 backdrop-blur-sm px-3 py-1 rounded-full text-xs font-semibold mb-4">
              <Brain className="w-3.5 h-3.5" />
              Sessão de Extração do Tácito
            </div>
            <h1 className="text-2xl sm:text-3xl font-bold mb-3 leading-tight">
              O que a IA não consegue extrair,
              <br />
              <span className="text-indigo-200">esta sessão extrai.</span>
            </h1>
            <p className="text-indigo-100 text-sm leading-relaxed max-w-lg">
              Um Meet de 1 a 2 horas com um estrategista da eBuz. Usando técnicas
              de extração de conhecimento tácito, capturamos o que você não sabe
              que sabe: suas decisões automáticas, suas histórias, sua voz real.
              De lá sai o Mapa do Ecossistema completo.
            </p>
          </div>

          <div className="px-8 py-6 space-y-6">
            <div>
              <h2 className="text-stone-900 font-semibold text-sm mb-4">O que você recebe:</h2>
              <div className="space-y-4">
                {BENEFITS.map((b) => (
                  <div key={b.title} className="flex items-start gap-3">
                    <div className="w-9 h-9 rounded-xl bg-indigo-50 border border-indigo-200 flex items-center justify-center shrink-0 mt-0.5">
                      <b.icon className="w-4 h-4 text-indigo-500" />
                    </div>
                    <div>
                      <p className="text-stone-900 text-sm font-semibold">{b.title}</p>
                      <p className="text-stone-500 text-xs leading-relaxed mt-0.5">{b.desc}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            <div className="bg-indigo-50 border border-indigo-200 rounded-xl px-4 py-3">
              <p className="text-indigo-800 text-xs leading-relaxed">
                <strong>Pré-requisito:</strong> ter completado o Extrator de Contexto
                no app. Quanto mais contexto você trouxer, mais profunda fica a sessão.
                Se ainda não fez,{' '}
                <Link href="/app/passaporte" className="text-indigo-600 underline hover:text-indigo-500">
                  comece aqui gratuitamente
                </Link>.
              </p>
            </div>

            <div className="bg-stone-50 border border-stone-200 rounded-xl px-4 py-3">
              <p className="text-stone-600 text-xs leading-relaxed">
                <strong>Como funciona:</strong> Após o pagamento, a agenda libera
                automaticamente. Você escolhe o melhor horário. O estrategista já
                terá lido seu Mapa de Contexto e preparado perguntas personalizadas
                para a sessão.
              </p>
            </div>
          </div>
        </div>

        {/* Prova social / diferencial */}
        <div className="bg-white rounded-2xl border border-stone-200 shadow-sm p-6">
          <h3 className="text-stone-900 font-semibold text-sm mb-3">Por que essa sessão é diferente?</h3>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            <div className="text-center">
              <p className="text-2xl font-black text-indigo-600">1-2h</p>
              <p className="text-stone-500 text-xs mt-1">de extração profunda</p>
            </div>
            <div className="text-center">
              <p className="text-2xl font-black text-indigo-600">15+</p>
              <p className="text-stone-500 text-xs mt-1">personas mapeadas</p>
            </div>
            <div className="text-center">
              <p className="text-2xl font-black text-indigo-600">18</p>
              <p className="text-stone-500 text-xs mt-1">iscas sugeridas</p>
            </div>
          </div>
        </div>
      </div>

      {/* Coluna direita: formulário */}
      <div className="lg:col-span-2">
        <form
          onSubmit={handleSubmit}
          className="bg-white rounded-2xl border border-stone-200 shadow-sm p-6 space-y-4 sticky top-6"
        >
          <div className="text-center pb-4 border-b border-stone-100">
            <div className="flex items-baseline justify-center gap-2">
              <span className="text-stone-500 text-sm">por</span>
              <span className="text-3xl font-bold text-stone-900">R$ 500</span>
            </div>
            <p className="text-stone-400 text-xs mt-1">pagamento único</p>
          </div>

          <h2 className="text-stone-900 font-bold text-lg">Seus dados</h2>

          <div>
            <label className="block text-stone-700 text-xs font-semibold mb-1">E-mail</label>
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              disabled={isLoggedIn}
              required
              placeholder="seu@email.com"
              className={`w-full px-3 py-2 border border-stone-200 rounded-lg text-sm focus:outline-none focus:border-indigo-400 ${
                isLoggedIn ? 'bg-stone-100 text-stone-500' : 'bg-white text-stone-900'
              }`}
            />
          </div>

          <div>
            <label className="block text-stone-700 text-xs font-semibold mb-1">Nome completo</label>
            <input
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              required
              className="w-full px-3 py-2 bg-white border border-stone-200 rounded-lg text-sm focus:outline-none focus:border-indigo-400"
            />
          </div>

          <div>
            <label className="block text-stone-700 text-xs font-semibold mb-1">Telefone (com DDD)</label>
            <input
              type="tel"
              value={phone}
              onChange={(e) => setPhone(e.target.value)}
              required
              placeholder="11 99999-9999"
              className="w-full px-3 py-2 bg-white border border-stone-200 rounded-lg text-sm focus:outline-none focus:border-indigo-400"
            />
          </div>

          <div>
            <label className="block text-stone-700 text-xs font-semibold mb-1">CPF/CNPJ</label>
            <input
              type="text"
              value={cpfCnpj}
              onChange={(e) => setCpfCnpj(e.target.value)}
              required
              placeholder="000.000.000-00"
              className="w-full px-3 py-2 bg-white border border-stone-200 rounded-lg text-sm focus:outline-none focus:border-indigo-400"
            />
          </div>

          <div>
            <label className="block text-stone-700 text-xs font-semibold mb-2">Forma de pagamento</label>
            <div className="grid grid-cols-3 gap-2">
              {(['PIX', 'CREDIT_CARD', 'BOLETO'] as BillingType[]).map((b) => (
                <button
                  key={b}
                  type="button"
                  onClick={() => setBillingType(b)}
                  className={`px-3 py-2 rounded-lg text-xs font-semibold transition-colors border ${
                    billingType === b
                      ? 'bg-indigo-600 text-white border-indigo-600'
                      : 'bg-white text-stone-600 border-stone-200 hover:border-stone-300'
                  }`}
                >
                  {b === 'PIX' ? 'PIX' : b === 'CREDIT_CARD' ? 'Cartão' : 'Boleto'}
                </button>
              ))}
            </div>
          </div>

          {error && (
            <div className="bg-red-50 border border-red-200 rounded-lg px-3 py-2">
              <p className="text-red-700 text-xs">{error}</p>
            </div>
          )}

          <button
            type="submit"
            disabled={loading}
            className="w-full bg-indigo-600 hover:bg-indigo-700 disabled:opacity-60 text-white font-semibold px-6 py-3 rounded-xl transition-colors text-sm flex items-center justify-center gap-2"
          >
            {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : null}
            {loading ? 'Processando...' : 'Pagar R$ 500 e agendar'}
          </button>

          <p className="text-stone-400 text-[11px] text-center">
            Após confirmar o pagamento, a agenda libera automaticamente.{' '}
            <Link href="/agenda" className="text-indigo-600 hover:underline">
              Voltar
            </Link>
          </p>
        </form>
      </div>
    </div>
  )
}
